import { DestroyRef, Injectable } from '@angular/core';

/** Закрывает предыдущий открытый select/календарь при открытии нового. */
@Injectable({ providedIn: 'root' })
export class OverlayCoordinatorService {
  private activeClose: (() => void) | null = null;
  private readonly predicates = new Set<OverlayPredicate>();

  open(close: () => void): void {
    if (this.activeClose && this.activeClose !== close) {
      this.activeClose();
    }
    this.activeClose = close;
  }

  close(close: () => void): void {
    if (this.activeClose === close) {
      this.activeClose = null;
    }
  }

  /**
   * BE-1239: способ рассказать о своей открытости тем поповерам, которые не отдают сюда
   * функцию закрытия. По нему модальное окно понимает, что Esc принадлежит раскрытому
   * внутри него списку, а не окну.
   *
   * Регистрируется ПРЕДИКАТ, а не пара open/close: у автокомплита признак открытости
   * меняется в десятке мест, и парные вызовы неизбежно разъехались бы. Забытый close()
   * означал бы, что координатор навсегда считает поповер открытым и Esc перестаёт
   * закрывать окна вообще, — цена рассинхрона здесь выше, чем у закрытия предыдущего
   * оверлея.
   *
   * Если передан `destroyRef` — снятие произойдёт само при уничтожении владельца; иначе
   * вызывать возвращённую функцию вручную. Тот же контракт, что у
   * `UnsavedChangesService.register` и `ConfirmationLockService.acquire`.
   */
  registerOpenPredicate(
    isOpen: () => boolean,
    close: () => void,
    destroyRef?: DestroyRef,
  ): () => void {
    const entry: OverlayPredicate = { isOpen, close };
    this.predicates.add(entry);

    const unregister = () => this.predicates.delete(entry);
    destroyRef?.onDestroy(unregister);
    return unregister;
  }

  /**
   * BE-1239: закрыть раскрытый поповер и сообщить, было ли что закрывать. По ответу
   * модальное окно понимает, достался Esc поповеру или окну.
   *
   * Закрываем сами, а не полагаемся на обработчик поповера: своего Esc есть не у всех —
   * календарь `ui-date-picker` перехватить его не может, потому что фокус остаётся на
   * поле ввода родителя. Без этого Esc не делал бы ничего вообще: окно не закрывается,
   * потому что поповер раскрыт, а поповер не закрывается, потому что некому.
   *
   * Ссылку снимаем здесь же: закрывающая функция не обязана дёргать `close`, и
   * оставленная ссылка означала бы «оверлей открыт навсегда».
   */
  closeActive(): boolean {
    const close = this.activeClose;
    if (close) {
      this.activeClose = null;
      close();
      return true;
    }
    for (const entry of this.predicates) {
      if (entry.isOpen()) {
        entry.close();
        return true;
      }
    }
    return false;
  }
}

interface OverlayPredicate {
  isOpen: () => boolean;
  close: () => void;
}
